import React from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Calendar, FileText, FolderKanban, ShieldCheck, CalendarClock, Sparkles } from 'lucide-react';
import { getDayName } from '@/types/timesheetSettings';

interface TimesheetDataRequirementsSettingsProps {
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  showRole: boolean;
  requireRole: boolean;
  showCostCentre: boolean;
  requireCostCentre: boolean;
  projectSelectionScope: 'all' | 'assigned';
  showArchivedProjects: boolean;
  showProjectTimeRemaining: boolean;
  minimumEntryDuration: '6min' | '15min' | '30min' | '1hour';
  timeRounding: 'none' | '15min' | '30min' | '1hour';
  allowNegativeHours: boolean;
  historicalEntryLimit: number;
  allowFutureDatedEntries: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited';
  enableCopyPreviousWeek: boolean;
  copyDescriptions: boolean;
  copyOnlyBillable: boolean;
  enableAutoSmartFill: boolean;
  enableAiInsights: boolean;
  onWeekStartDayChange: (day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => void;
  onShowRoleChange: (show: boolean) => void;
  onRequireRoleChange: (require: boolean) => void;
  onShowCostCentreChange: (show: boolean) => void;
  onRequireCostCentreChange: (require: boolean) => void;
  requireDescription: 'never' | 'always';
  onRequireDescriptionChange: (value: 'never' | 'always') => void;
  onProjectSelectionScopeChange: (scope: 'all' | 'assigned') => void;
  onShowArchivedProjectsChange: (show: boolean) => void;
  onShowProjectTimeRemainingChange: (show: boolean) => void;
  onMinimumEntryDurationChange: (duration: '6min' | '15min' | '30min' | '1hour') => void;
  onTimeRoundingChange: (rounding: 'none' | '15min' | '30min' | '1hour') => void;
  onAllowNegativeHoursChange: (allow: boolean) => void;
  onHistoricalEntryLimitChange: (weeks: number) => void;
  onAllowFutureDatedEntriesChange: (allow: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited') => void;
  onEnableCopyPreviousWeekChange: (enable: boolean) => void;
  onCopyDescriptionsChange:(copy: boolean) => void;
  onCopyOnlyBillableChange: (copy: boolean) => void;
  onEnableAutoSmartFillChange: (enable: boolean) => void;
  onEnableAiInsightsChange: (enable: boolean) => void;
}

export function TimesheetDataRequirementsSettings({
  weekStartDay,
  showRole,
  requireRole,
  showCostCentre,
  requireCostCentre,
  projectSelectionScope,
  showArchivedProjects,
  showProjectTimeRemaining,
  minimumEntryDuration,
  timeRounding,
  allowNegativeHours,
  historicalEntryLimit,
  allowFutureDatedEntries,
  enableCopyPreviousWeek,
  copyDescriptions,
  copyOnlyBillable,
  enableAutoSmartFill,
  enableAiInsights,
  onWeekStartDayChange,
  onShowRoleChange,
  onRequireRoleChange,
  onShowCostCentreChange,
  onRequireCostCentreChange,
  requireDescription,
  onRequireDescriptionChange,
  onProjectSelectionScopeChange,
  onShowArchivedProjectsChange,
  onShowProjectTimeRemainingChange,
  onMinimumEntryDurationChange,
  onTimeRoundingChange,
  onAllowNegativeHoursChange,
  onHistoricalEntryLimitChange,
  onAllowFutureDatedEntriesChange,
  onEnableCopyPreviousWeekChange,
  onCopyDescriptionsChange,
  onCopyOnlyBillableChange,
  onEnableAutoSmartFillChange,
  onEnableAiInsightsChange,
}: TimesheetDataRequirementsSettingsProps) {
  return (
    <div className="space-y-6">
      {/* Week Configuration Section */}
      <div className="flex items-start gap-3 pb-4 border-b">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <Calendar className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">Week Configuration</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure how weeks are calculated across the application
          </p>
        </div>
      </div>

      {/* Week Start Day */}
      <div className="border rounded-lg p-4 space-y-2">
        <Label htmlFor="weekStartDay" className="text-sm font-medium">
          Week Start Day
        </Label>
        <Select
          value={weekStartDay.toString()}
          onValueChange={(value) => onWeekStartDayChange(parseInt(value) as 0 | 1 | 2 | 3 | 4 | 5 | 6)}
        >
          <SelectTrigger id="weekStartDay" className="h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Sunday</SelectItem>
            <SelectItem value="1">Monday (Recommended)</SelectItem>
            <SelectItem value="2">Tuesday</SelectItem>
            <SelectItem value="3">Wednesday</SelectItem>
            <SelectItem value="4">Thursday</SelectItem>
            <SelectItem value="5">Friday</SelectItem>
            <SelectItem value="6">Saturday</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Determines the starting day for timesheet weeks, weekly totals, and deadline calculations.
          Currently set to <strong>{getDayName(weekStartDay)}</strong>.
        </p>
      </div>

      {/* Project Selection Section */}
      <div className="flex items-start gap-3 pb-4 border-b pt-6">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <FolderKanban className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">Project Selection</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Control which projects employees can select when logging time
          </p>
        </div>
      </div>

      {/* Project Selection Settings */}
      <div className="border rounded-lg p-4 space-y-4">
        {/* Project Selection Scope */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <Label htmlFor="projectSelectionScope" className="text-sm font-medium">
              Project Selection Scope
            </Label>
            <p className="text-sm text-muted-foreground">
              Which projects can employees choose from
            </p>
          </div>
          <div className="w-64">
            <Select
              value={projectSelectionScope}
              onValueChange={(value) => onProjectSelectionScopeChange(value as 'all' | 'assigned')}
            >
              <SelectTrigger id="projectSelectionScope" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All active projects</SelectItem>
                <SelectItem value="assigned">Assigned projects only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Show Archived Projects */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="showArchivedProjects" className="text-sm font-medium cursor-pointer">
              Show Archived Projects
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Include archived/closed projects in selection list
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="showArchivedProjects"
              checked={showArchivedProjects}
              onCheckedChange={onShowArchivedProjectsChange}
            />
          </div>
        </div>

        {/* Show Project Time Remaining */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="showProjectTimeRemaining" className="text-sm font-medium cursor-pointer">
              Show Project Time Remaining
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Display budget remaining when selecting projects
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="showProjectTimeRemaining"
              checked={showProjectTimeRemaining}
              onCheckedChange={onShowProjectTimeRemainingChange}
            />
          </div>
        </div>
      </div>

      {/* Time Entry Required Fields Section */}
      <div className="flex items-start gap-3 pb-4 border-b pt-6">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <FileText className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">Time Entry Required Fields</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure which fields are shown and required to timesheet entry
          </p>
        </div>
      </div>

      {/* Required Fields Settings */}
      <div className="border rounded-lg p-4 space-y-4">
        {/* Show Role Field */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <Label htmlFor="showRole" className="text-sm font-medium cursor-pointer">
              Show Role Field
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Display role selector on timesheet entries
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="showRole"
              checked={showRole}
              onCheckedChange={onShowRoleChange}
            />
          </div>
        </div>

        {/* Require Role */}
        <div className="flex items-start justify-between gap-4 pl-4 pt-4 border-t">
          <div className="flex-1">
            <Label 
              htmlFor="requireRole" 
              className={`text-sm font-medium cursor-pointer ${!showRole ? 'text-muted-foreground' : ''}`}
            >
              Require Role
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Make role selection mandatory (only if shown)
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="requireRole"
              checked={requireRole}
              disabled={!showRole}
              onCheckedChange={onRequireRoleChange}
            />
          </div>
        </div>

        {/* Show Cost Centre Field */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="showCostCentre" className="text-sm font-medium cursor-pointer">
              Show Cost Centre Field
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Display cost centre selector on timesheet entries
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="showCostCentre"
              checked={showCostCentre}
              onCheckedChange={onShowCostCentreChange}
            />
          </div>
        </div>

        {/* Require Cost Centre */}
        <div className="flex items-start justify-between gap-4 pl-4 pt-4 border-t">
          <div className="flex-1">
            <Label 
              htmlFor="requireCostCentre" 
              className={`text-sm font-medium cursor-pointer ${!showCostCentre ? 'text-muted-foreground' : ''}`}
            >
              Require Cost Centre
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Make cost centre selection mandatory (only if shown)
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="requireCostCentre"
              checked={requireCostCentre}
              disabled={!showCostCentre}
              onCheckedChange={onRequireCostCentreChange}
            />
          </div>
        </div>

        {/* Require Description */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="requireDescription" className="text-sm font-medium">
              Require Description
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              When must entries include a description
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Select
              value={requireDescription}
              onValueChange={onRequireDescriptionChange}
            >
              <SelectTrigger id="requireDescription" className="w-[240px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Never required</SelectItem>
                <SelectItem value="always">Always required</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Time Entry Validation Section */}
      <div className="flex items-start gap-3 pb-4 border-b pt-6">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <ShieldCheck className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">Time Entry Validation</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Rules for how time can be entered and formatted
          </p>
        </div>
      </div>

      {/* Time Entry Validation Settings */}
      <div className="border rounded-lg p-4 space-y-4">
        {/* Minimum Entry Duration */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <Label htmlFor="minimumEntryDuration" className="text-sm font-medium">
              Minimum Entry Duration
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Smallest time increment that can be logged
            </p>
          </div>
          <div className="w-[240px]">
            <Select
              value={minimumEntryDuration}
              onValueChange={onMinimumEntryDurationChange}
            >
              <SelectTrigger id="minimumEntryDuration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6min">6 minutes (0.1 hours)</SelectItem>
                <SelectItem value="15min">15 minutes (0.25 hours)</SelectItem>
                <SelectItem value="30min">30 minutes (0.5 hours)</SelectItem>
                <SelectItem value="1hour">1 hour</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Time Rounding */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="timeRounding" className="text-sm font-medium">
              Time Rounding
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Automatically round time entries to nearest increment
            </p>
          </div>
          <div className="w-[240px]">
            <Select
              value={timeRounding}
              onValueChange={onTimeRoundingChange}
            >
              <SelectTrigger id="timeRounding">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (no rounding)</SelectItem>
                <SelectItem value="15min">15 minutes</SelectItem>
                <SelectItem value="30min">30 minutes</SelectItem>
                <SelectItem value="1hour">1 hour</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Allow Negative Hours */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="allowNegativeHours" className="text-sm font-medium cursor-pointer">
              Allow Negative Hours
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Permit negative values for corrections/adjustments
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="allowNegativeHours"
              checked={allowNegativeHours}
              onCheckedChange={onAllowNegativeHoursChange}
            />
          </div>
        </div>
      </div>

      {/* Date Range Restrictions Section */}
      <div className="flex items-start gap-3 pb-4 border-b pt-6">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <CalendarClock className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">Date Range Restrictions</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Control how far back or forward employees can log time
          </p>
        </div>
      </div>

      {/* Date Range Restrictions Settings */}
      <div className="border rounded-lg p-4 space-y-4">
        {/* Historical Entry Limit */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <Label htmlFor="historicalEntryLimit" className="text-sm font-medium">
              Historical Entry Limit
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              How many weeks back can time be entered
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              id="historicalEntryLimit"
              type="number"
              min={1}
              max={52}
              value={historicalEntryLimit}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value) && value >= 1 && value <= 52) {
                  onHistoricalEntryLimitChange(value);
                }
              }}
              className="w-20 text-center"
            />
            <span className="text-sm text-muted-foreground">weeks back</span>
          </div>
        </div>

        {/* Allow Future-Dated Entries */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="allowFutureDatedEntries" className="text-sm font-medium">
              Allow Future-Dated Entries
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Can employees log time for future dates
            </p>
          </div>
          <div className="w-[240px]">
            <Select
              value={allowFutureDatedEntries}
              onValueChange={onAllowFutureDatedEntriesChange}
            >
              <SelectTrigger id="allowFutureDatedEntries">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No (current week only)</SelectItem>
                <SelectItem value="1week">Yes, up to 1 week ahead</SelectItem>
                <SelectItem value="2weeks">Yes, up to 2 weeks ahead</SelectItem>
                <SelectItem value="4weeks">Yes, up to 4 weeks ahead</SelectItem>
                <SelectItem value="unlimited">Yes, unlimited</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Smart Features Section */}
      <div className="flex items-start gap-3 pb-4 border-b pt-6">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <Sparkles className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">Smart Features</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Enable intelligent tools to streamline time entry and provide insights
          </p>
        </div>
      </div>

      {/* Smart Features Settings */}
      <div className="border rounded-lg p-4 space-y-4">
        {/* Copy Previous Week */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <Label htmlFor="enable-copy-previous-week" className="text-sm font-medium cursor-pointer">
              Enable 'Copy Previous Week'
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Show button to copy entries from previous week
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="enable-copy-previous-week"
              checked={enableCopyPreviousWeek}
              onCheckedChange={onEnableCopyPreviousWeekChange}
            />
          </div>
        </div>

        {/* Copy Descriptions */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="copy-descriptions" className="text-sm font-medium cursor-pointer">
              Copy Descriptions
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Include entry descriptions when copying
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="copy-descriptions"
              checked={copyDescriptions}
              onCheckedChange={onCopyDescriptionsChange}
            />
          </div>
        </div>

        {/* Copy Only Billable Entries */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="copy-only-billable" className="text-sm font-medium cursor-pointer">
              Copy Only Billable Entries
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              When copying, exclude non-billable time
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="copy-only-billable"
              checked={copyOnlyBillable}
              onCheckedChange={onCopyOnlyBillableChange}
            />
          </div>
        </div>

        {/* Enable Auto Smart Fill */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="enable-auto-smart-fill" className="text-sm font-medium cursor-pointer">
              Enable Auto Smart Fill
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Automatically suggest time entries based on patterns
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="enable-auto-smart-fill"
              checked={enableAutoSmartFill}
              onCheckedChange={onEnableAutoSmartFillChange}
            />
          </div>
        </div>

        {/* Enable AI Insights */}
        <div className="flex items-start justify-between gap-4 pt-4 border-t">
          <div className="flex-1">
            <Label htmlFor="enable-ai-insights" className="text-sm font-medium cursor-pointer">
              Enable AI Insights
            </Label>
            <p className="text-sm text-muted-foreground mt-1">
              Show AI-powered insights and anomaly detection
            </p>
          </div>
          <div className="flex items-center pt-0.5">
            <Checkbox
              id="enable-ai-insights"
              checked={enableAiInsights}
              onCheckedChange={onEnableAiInsightsChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
